import type { CreditBalance } from '@/@credit-consumption/types/balance';
import {
    PAUSE_REASONS,
    paceCopy,
    pauseCopy,
    trialStripCopy,
    type PauseCopy,
    type PauseReason,
    type TrialStripCopy,
} from '@/lib/credits/credit-vocabulary';

import type { CreditGateData } from '../types';

/**
 * The chat's credit alerts for an org on the new balance (M4S-5895, plan step 27;
 * docs/pdlc/credits-ui.md §7 "Chat", §2.7). Pure: the hook `useStudioCreditAlerts` feeds it the
 * balance response and the chat's own 403, and renders what comes back.
 *
 * Only for `reader: 'ledger'`. An org not yet on the new balance keeps today's chat alerts
 * (the monthly-cap copy with its reset date) until the cleanup.
 *
 * - **Pause.** The chat's 403 decides first (it is the gate's own answer); otherwise the balance's
 *   `pause`. The copy is `pauseCopy`, so it names the reason and the one contact and ends
 *   "See credits →". The monthly-cap reasons do not exist on the new balance: a stale one (from
 *   the tab's stored gate) reads as no credits left. A plan end is never filled in from the
 *   contract end date: only the gate's own `licenseEndDate` names one.
 * - **Pace.** A dismissible warning replaces the monthly one, when the balance's pace warns and
 *   the run-out date is within 30 days.
 * - **Trial strip.** A trial on credits shows its credits left and end date where the old message
 *   counter sat.
 */

/** The pace warning shows only when the balance runs out within this many days. */
export const PACE_WARNING_WITHIN_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

/** Who reads the chat: a client org admin (and staff under View-as, D13) or a member. */
export type ChatCreditAudience = 'admin' | 'member';

export interface LedgerChatAlertInput {
    balance: CreditBalance;
    /** The chat's own 403 for this tab, when one arrived. */
    gate: CreditGateData | null;
    audience: ChatCreditAudience;
    now: Date;
}

export interface LedgerChatAlerts {
    isPaused: boolean;
    pauseReason: PauseReason | null;
    /** The pause banner: title, body and the "See credits" link. */
    pause: PauseCopy | null;
    /** The input's placeholder while paused. */
    placeholder: string | null;
    /** The dismissible pace warning, or null. */
    paceWarning: string | null;
    trialStrip: TrialStripCopy | null;
}

const SHOWN_REASONS: readonly string[] = PAUSE_REASONS.filter((r) => r !== 'LEDGER_NOT_LIVE');

/** A reason as the chat shows it on the new balance; null for one it cannot show. */
export function ledgerChatReason(reason: string | null | undefined): PauseReason | null {
    if (!reason) return null;
    // The new balance has no monthly cap and no annual pool (§2.7): both are "no credits left".
    if (reason === 'monthly_cap_exceeded' || reason === 'annual_pool_exceeded') return 'no_credits';
    return SHOWN_REASONS.includes(reason) ? (reason as PauseReason) : 'no_credits';
}

function dateFor(
    reason: PauseReason,
    balance: CreditBalance,
    gate: CreditGateData | null,
): string | null {
    if (reason === 'license_expired') return gate?.licenseEndDate ?? null;
    if (reason === 'trial_ended') {
        return balance.endDateKind === 'trial' && balance.endDate
            ? balance.endDate
            : (gate?.licenseEndDate ?? null);
    }
    return null;
}

/** Whether the pace warning is due: the API's pace warns and the run-out is within 30 days. */
export function paceWarningDue(balance: CreditBalance, now: Date): boolean {
    const { pace } = balance;
    if (balance.unlimited || !pace?.warn || !pace.runOutOn) return false;
    const runOut = Date.parse(pace.runOutOn);
    if (Number.isNaN(runOut)) return false;
    return runOut - now.getTime() <= PACE_WARNING_WITHIN_DAYS * DAY_MS;
}

export function ledgerChatAlerts(input: LedgerChatAlertInput): LedgerChatAlerts {
    const { balance, gate, audience, now } = input;
    const pauseReason =
        ledgerChatReason(gate?.reason) ??
        (balance.pause?.paused ? (ledgerChatReason(balance.pause.reason) ?? 'no_credits') : null);
    const isPaused = pauseReason != null;

    const pause = pauseReason
        ? pauseCopy(pauseReason, {
              audience,
              orgName: balance.orgName || undefined,
              resetDate: gate?.resetDate ?? balance.pause?.resetDate ?? null,
              date: dateFor(pauseReason, balance, gate),
              limit: gate?.userSpendLimit,
          })
        : null;

    const paceWarning =
        !isPaused && paceWarningDue(balance, now)
            ? paceCopy({
                  basisDays: balance.pace.basisDays,
                  perWeek: balance.pace.avgDaily * 7,
                  runsOutOn: balance.pace.runOutOn,
                  runsOutBeforeEnd:
                      !!balance.endDate &&
                      !!balance.pace.runOutOn &&
                      Date.parse(balance.pace.runOutOn) < Date.parse(balance.endDate),
                  available: balance.available,
              }).warning
            : null;

    const trialStrip =
        balance.planKind === 'trial' && !balance.unlimited
            ? trialStripCopy({
                  available: balance.available,
                  endDate: balance.endDateKind === 'trial' ? balance.endDate : null,
              })
            : null;

    return {
        isPaused,
        pauseReason,
        pause,
        placeholder: pause?.title ?? null,
        paceWarning,
        trialStrip,
    };
}
